import { AxiosError } from 'axios';
import { getRegionsWithNameUrl, getUserRegionUrl } from 'constants/urls';
import { UserContext } from 'context/UserContext';
import { useSWRAxios } from 'hooks/useSWRAxios';
import { Region } from 'pages/Map/types';
import { useContext, useEffect, useState } from 'react';
import { getReferenceRegionNames } from './utils';

export const useRegion = (userId: string, regionId: string) => {
    const shouldFetch = userId && regionId;
    const url = getUserRegionUrl(userId, regionId);
    const [region, setRegion] = useState<Region>();
    const [refRegionNames, setRefRegionNames] = useState<string[]>([]);
    const { data, isLoading, ...restProperties } = useSWRAxios<
        Region,
        AxiosError<{ message: string }>,
        {
            fallbackData: Region;
        }
    >(shouldFetch ? url : null);

    // fetches if any reference regions are exists in region
    const { regions, isLoading: refZonesLoading } = useRegionsByName(userId, refRegionNames);

    useEffect(() => {
        if (!regions || !regions.length) {
            return;
        }
        const region = {
            ...data,
            zones: data.zones?.map(zone => {
                if (zone.ref) {
                    const region = regions.find(region => zone.ref && region.name === zone.ref.region);
                    const zoneDetials = region ? region.zones.find(zoneRef => zoneRef.type === zone.type) : {};

                    return {
                        ...zone,
                        ...zoneDetials,
                        ref: { ...zone.ref, regionId: region?.id as string }
                    };
                }
                return zone;
            })
        };
        setRegion(region);
    }, [regions, data]);

    useEffect(() => {
        if (!data) {
            return;
        }
        const refZoneNames = getReferenceRegionNames(data);
        setRegion(data);
        if (refRegionNames) {
            setRefRegionNames(refZoneNames);
        }
    }, [data]);

    return {
        ...restProperties,
        isLoading: isLoading || refZonesLoading,
        isAllRefZonesFetched: refRegionNames.length ? !!regions?.length : true,
        region
    };
};

export const useAssignedRegion = () => {
    const { user } = useContext(UserContext);

    const userId = user?.id || '';
    const regionId = user?.assignedRegions ? user?.assignedRegions[0].id : ''; //We are supporting only 1 region assignment atm
    return useRegion(userId, regionId);
};

export const useRegionsByName = (userId: string, names: string[]) => {
    const regionNames = names.join(',');
    const shouldFetch = !!userId && !!regionNames;
    const { data, ...restProperties } = useSWRAxios<
        Region[],
        AxiosError<{ message: string }>,
        {
            fallbackData: Region[];
        }
    >(shouldFetch ? getRegionsWithNameUrl(userId, regionNames) : null);
    return {
        ...restProperties,
        regions: shouldFetch ? data : undefined
    };
};
